import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetAssemblyDocumentsBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import AssemblyDocumentModal from './AssemblyDocumentModal';

import global from "../../../../../global.scss";

const AssemblyDocumentPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [documentsList, setDocumentsList] = useState([]);
  const [documentsTotal, setDocumentsTotal] = useState(0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "Session id", sort: "ref_session_id" },
    { label: "Doc type", sort: "doc_type" },
    { label: "File name", sort: "file_name" },
    { label: "Extraction status", sort: "extraction_status" },
    { label: "Completeness %", sort: "completeness_pct" },
    { label: "Has error", sort: "has_error" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setDocumentsList(state.assemblyDocumentsList.records || []);
      setDocumentsTotal(state.assemblyDocumentsList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetDocuments(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetAssemblyDocumentsBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  return (
    <Box>

      <AssemblyDocumentModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetDocuments}
      />

      <TopBar
        title={"Assembly documents"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        // button1Text={"Add assembly document"}
        // button1OnClick={() => {
        //   setUpdate({ defaultMode: 1 });
        //   setModalTitle("Add assembly document");
        //   setModalOpen(!modalOpen);
        // }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          documentsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No assembly document found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                documentsList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.ref_session_id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.doc_type || <NA />}</TableCell>
                    <TableCell>{row.file_name || <NA />}</TableCell>
                    <TableCell>{row.extraction_status || <NA />}</TableCell>
                    <TableCell>
                      {row.completeness_pct !== null && row.completeness_pct !== undefined
                        ? `${row.completeness_pct}%`
                        : <NA />}
                    </TableCell>
                    <TableCell>
                      {
                        row.has_error === true
                        ? <Chip size="small" color="error" label="Yes" />
                        : row.has_error === false
                          ? <Chip size="small" color="success" label="No" />
                          : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps createdAt={row.created_at} />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.file_name || row.id || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={9}
                  count={documentsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit assembly document");
            setUpdate({ ...update, defaultMode: 2 });
            setMenuOpen(false);
            setModalOpen(!modalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default AssemblyDocumentPage;
